import { XMarkIcon } from '@heroicons/react/20/solid'
import { twMerge } from 'tailwind-merge'

import Action from '@/gf/components/Action'
import Box from '@/gf/components/Box'
import Link from '@/gf/components/Link'
import QuoteUploadButton, { QuoteUploadProps } from './QuoteUploadButton'
import useQuoteUpload from './useQuoteUpload'

const AiAssistantCallout = ({ onTryAiAssistant }: { onTryAiAssistant: () => void }) => (
  <Box className="flex grow flex-col sm:flex-row items-center gap-4 px-4 py-3 bg-amber-50 border-amber-200">
    <div className="flex grow flex-col gap-y-1 text-gray-900">
      <p className="font-medium text-base">Something look off?</p>
      <p className="text-sm">
        If the extracted data doesn&apos;t look right, try the Quote Assistant to help fix it.
      </p>
    </div>
    <div className="flex shrink-0 gap-2">
      <Action.S onClick={onTryAiAssistant}>Try Quote Assistant</Action.S>
    </div>
  </Box>
)

const QuoteUploadBar = ({
  onError,
  onUploadComplete,
  onUploadStarted,
  rfqId,
  cantParticipateLinkTo,
  hasQuoteUpload,
}: QuoteUploadProps & {
  cantParticipateLinkTo?: string
  hasQuoteUpload?: boolean
}) => {
  const { onTryAiAssistant, hasUploadedThisSession } = useQuoteUpload()

  // Show callout if there is an existing upload from the query, or one uploaded this session
  const showAiCallout = hasQuoteUpload || hasUploadedThisSession

  return (
    <div className="flex flex-col gap-2">
      <Box className="flex grow flex-col lg:flex-row items-center">
        <div
          className={twMerge(
            'px-3 xl:px-6 py-3 lg:py-4 flex grow flex-col xl:flex-row gap-x-6 gap-y-2 items-center lg:items-start xl:items-center',
            cantParticipateLinkTo && 'border-0 lg:border-r'
          )}
        >
          <div className="flex grow flex-col gap-y-2 text-gray-900">
            <p className="font-medium text-base">Upload Quote</p>
            <p className="text-sm">
              Already created this quote in another application? Upload the file to add the line
              items here.
            </p>
          </div>
          <div className="flex shrink-0">
            <QuoteUploadButton
              onError={onError}
              onUploadComplete={onUploadComplete}
              onUploadStarted={onUploadStarted}
              rfqId={rfqId}
            />
          </div>
        </div>
        {cantParticipateLinkTo && (
          <div className="w-full px-3 xl:px-6 py-3 lg:py-4 flex flex-1 items-center justify-center border-t lg:border-0">
            <Link.S
              to="cant-participate"
              className="flex gap-x-1.5 items-center shrink-0 whitespace-nowrap"
            >
              <XMarkIcon className="-mx-0.5 h-4 w-4" /> Can&apos;t Participate
            </Link.S>
          </div>
        )}
      </Box>
      {showAiCallout && <AiAssistantCallout onTryAiAssistant={onTryAiAssistant} />}
    </div>
  )
}

export default QuoteUploadBar
